import {buildUrl, snakeToCamel, toSnakeCase, camelToSnake} from 'utils/common';

const config = {
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  mode: 'cors',
};

export const setAuthHeader = (token) => {
  if (token) {
    config.headers['Authorization'] = `JWT ${token}`;
  } else {
    delete config.headers['Authorization'];
  }
};

const preprocess = (response) => {
  if (response.status === 204) {
    return Promise.resolve({
      status: response.status,
    });
  } else if (response.status >= 100 && response.status <= 399) {
    return response.json().then((data) => snakeToCamel(data));
  } else {
    return response.json().then((data) =>
      Promise.reject(
        snakeToCamel({
          ...data,
          status: response.status,
        }),
      ),
    );
  }
};

const getJSON = (url, query = {}) => fetch(buildUrl(url, query), config).then(preprocess);

const postJSON = (url, payload, query = {}) =>
  fetch(buildUrl(url, query), {
    ...config,
    method: 'POST',
    body: JSON.stringify(payload),
  }).then(preprocess);

const putJSON = (url, payload, query = {}) =>
  fetch(buildUrl(url, query), {
    ...config,
    method: 'PUT',
    body: JSON.stringify(payload),
  }).then(preprocess);

const postForm = (url, formData, query = {}) =>
  fetch(buildUrl(url, query), {
    ...config,
    headers: {
      Accept: config.headers['Accept'],
      Authorization: config.headers['Authorization'],
    },
    method: 'POST',
    body: formData,
  }).then(preprocess);

const putForm = (url, formData, query = {}) =>
  fetch(buildUrl(url, query), {
    ...config,
    headers: {
      Accept: config.headers['Accept'],
      Authorization: config.headers['Authorization'],
    },
    method: 'PUT',
    body: formData,
  }).then(preprocess);

const deleteReq = (url, query = {}) =>
  fetch(buildUrl(url, query), {
    ...config,
    method: 'DELETE',
  }).then(preprocess);

const fetchList = (url, q, page = 1, perPage = 10, sortOpts = {}, extraParams = {}) =>
  getJSON(url, {
    q,
    page,
    per_page: perPage,
    sort_field: typeof sortOpts.sortField === 'string' ? toSnakeCase(sortOpts.sortField) : undefined,
    sort_dir: sortOpts.sortDir,
    ...camelToSnake(extraParams),
  });

const fetchDetail = (url) => getJSON(url, {detailed: true});

const fetchListEnhanced = ({url, q, page = 1, perPage = 10, sortOpts = {}, extraParams = {}}, signal) =>
  getJSONEnhanced(
    url,
    {
      q,
      page,
      per_page: perPage,
      sort_field: typeof sortOpts.sortField === 'string' ? toSnakeCase(sortOpts.sortField) : undefined,
      sort_dir: sortOpts.sortDir,
      ...camelToSnake(extraParams),
    },
    signal,
  );

const getJSONEnhanced = (url, query = {}, signal) => fetch(buildUrl(url, query), {...config, signal}).then(preprocess);

const fetchAll = async ({url, q, sortOpts = {}, extraParams = {}}) => {
  const records = [];
  const perPage = 100;
  let hasNext = true;
  let page = 1;
  while (hasNext) {
    const {records: newRecords, pageCount} = await fetchListEnhanced({url, q, page, perPage, sortOpts, extraParams});
    records.push(...newRecords);
    hasNext = pageCount > page;
    ++page;
  }
  return {records};
};

export default {
  getJSON,
  postJSON,
  putJSON,
  postForm,
  putForm,
  deleteReq,
  fetchList,
  fetchDetail,
  fetchListEnhanced,
  getJSONEnhanced,
  fetchAll,
};
